import * as React from "react"
import { cn } from "@/lib/utils"
import { CloseIcon } from "@/ui/components/icons/social"

export interface WalletAddressInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Display variant for different input types
   * @default 'wallet-address'
   */
  variant?: 'wallet-address' | 'name-service'
  /**
   * Callback when the clear button is clicked
   */
  onClear?: () => void
  /**
   * Label text to display above the input
   * Auto-generated based on variant if not provided
   */
  label?: string
  /**
   * Whether to show the close/clear button on the right
   * @default true
   */
  showClearButton?: boolean
}

/**
 * Wallet address input with two-line layout (label + editable value)
 *
 * Designed for entering wallet addresses or name service addresses (ENS, SNS, etc.)
 * Features a clear button on the right side to reset the input.
 * Follows ANYW3AR Figma design specifications (node-id: 260:498, 260:505).
 *
 * @example
 * ```tsx
 * // Wallet address variant
 * <WalletAddressInput
 *   variant="wallet-address"
 *   value="aVudDp3w...GocQz4nB"
 *   placeholder="Enter wallet address"
 *   onClear={() => console.log('cleared')}
 * />
 *
 * // Name service variant (ENS)
 * <WalletAddressInput
 *   variant="name-service"
 *   value="ipsteir.sol"
 *   placeholder="Enter name service address"
 * />
 * ```
 */
export const WalletAddressInput = React.forwardRef<HTMLInputElement, WalletAddressInputProps>(
  ({ className, variant = 'wallet-address', onClear, label, showClearButton = true, ...props }, ref) => {
    // Auto-generate label based on variant
    const displayLabel = label || (variant === 'wallet-address' ? 'Wallet address :' : 'SNS :')

    return (
      <div
        className={cn(
          "flex items-center justify-between",
          "px-2 py-0 rounded-[0.3125rem]",
          "bg-white border border-gray-100 border-solid",
          "transition-colors",
          className
        )}
      >
        {/* Left side: Two-line label + input */}
        <div className="flex gap-2 items-center flex-1 min-w-0">
          <div className="flex flex-col gap-[0.25rem] items-start flex-1 min-w-0">
            {/* Label (12px) */}
            <label
              className={cn(
                "font-montserrat text-[0.75rem] font-normal leading-5",
                "text-gray-500"
              )}
            >
              {displayLabel}
            </label>

            {/* Input Field (15px) */}
            <input
              ref={ref}
              className={cn(
                "w-full bg-transparent",
                "font-montserrat text-[0.9375rem] font-normal leading-5",
                "text-gray-500 placeholder:text-gray-500",
                "focus:text-gray-900 focus:outline-none",
                "truncate"
              )}
              {...props}
            />
          </div>
        </div>

        {/* Right Icon: Clear Button */}
        {showClearButton && (
          <button
            type="button"
            onClick={onClear}
            className={cn(
              "flex items-center py-2 shrink-0",
              "text-gray-500 hover:text-gray-900",
              "transition-colors cursor-pointer",
              "focus:outline-none"
            )}
            aria-label="Clear input"
          >
            <CloseIcon size={12} />
          </button>
        )}
      </div>
    )
  }
)

WalletAddressInput.displayName = "WalletAddressInput"
